// src/components/FixtureCard.jsx
import React, { useState } from 'react';
import FormIndicator from './FormIndicator';
import WeatherBadge from './WeatherBadge';
import MatchAnalyticsModal from './MatchAnalyticsModal';
import { Button, Card } from '../ui';

/**
 * FixtureCard - Enhanced fixture display with inline analytics
 * @param {object} fixture - Fixture object
 * @param {object} analytics - Analytics data (form, weather, etc.)
 * @param {function} onClick - Optional custom click handler
 * @param {boolean} showAnalytics - Whether to show inline analytics (default: true)
 * @param {string} className - Additional CSS classes
 */
export default function FixtureCard({ 
  fixture, 
  analytics, 
  onClick, 
  showAnalytics = true,
  className = '' 
}) {
  const [modalOpen, setModalOpen] = useState(false);

  if (!fixture) return null;

  const {
    home_team,
    away_team,
    kickoff_time,
    venue,
    home_score,
    away_score,
    status,
    league
  } = fixture;

  const { homeForm, awayForm, weather } = analytics || {};

  // Format kickoff time
  const kickoffDate = kickoff_time ? new Date(kickoff_time) : null;
  const timeStr = kickoffDate ? kickoffDate.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit'
  }) : 'TBD';

  // Handle card click
  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      setModalOpen(true);
    }
  };

  // Check if match is completed, abandoned, or postponed
  const isCompleted = status === 'completed' || status === 'complete' || (home_score !== null && away_score !== null);
  const isAbandoned = status === 'abandoned';
  const isPostponed = status === 'postponed';

  return (
    <>
      <Card as="article"
        className={`
          fc-fixture-card
          hover:shadow-lg hover:scale-[1.02] transition-all duration-200
          border border-ui-border
          ${isAbandoned ? 'border-red-300 bg-red-50' : ''}
          ${isPostponed ? 'border-yellow-300 bg-yellow-50' : ''}
          ${className}
        `}
      >
        {/* Abandoned/Postponed Banner */}
        {(isAbandoned || isPostponed) && (
          <div className={`mb-2 text-center text-xs font-semibold py-1 rounded ${
            isAbandoned ? "bg-red-200 text-ui-error" : 'bg-yellow-200 text-yellow-800'
          }`}>
            {isAbandoned ? '🚫 ABANDONED' : '⏸️ POSTPONED'}
          </div>
        )}
        
        {/* Main Fixture Display */}
        <div className="flex justify-between items-center gap-2 sm:gap-3">
          {/* Home Team */}
          <div className="flex-1 min-w-0">
            <div className="font-medium text-content-primary text-xs sm:text-sm mb-1 truncate">
              {home_team}
            </div>
            {showAnalytics && homeForm?.results && (
              <FormIndicator results={homeForm.results} maxResults={5} />
            )}
          </div>

          {/* Center: Time or Score */}
          <div className="px-1 sm:px-4 text-center min-w-[58px] sm:min-w-[80px] flex-shrink-0">
            {isAbandoned || isPostponed ? (
              <>
                <div className="text-base sm:text-lg font-bold text-content-secondary">
                  - - -
                </div>
                <div className="text-xs text-content-secondary mt-0.5">{isAbandoned ? 'Abandoned' : 'Postponed'}</div>
              </>
            ) : isCompleted ? (
              <>
                <div className="text-base sm:text-lg font-bold text-action-primary">
                  {home_score} - {away_score}
                </div>
                <div className="text-xs text-content-secondary mt-0.5">Full Time</div>
              </>
            ) : (
              <>
                <div className="text-base sm:text-lg font-bold text-content-secondary">
                  {timeStr}
                </div>
                <div className="text-xs text-content-secondary mt-0.5">Kick Off</div>
              </>
            )}
          </div>

          {/* Away Team */}
          <div className="flex-1 min-w-0 text-right">
            <div className="font-medium text-content-primary text-xs sm:text-sm mb-1 truncate">
              {away_team}
            </div>
            {showAnalytics && awayForm?.results && (
              <div className="flex justify-end">
                <FormIndicator results={awayForm.results} maxResults={5} />
              </div>
            )}
          </div>
        </div>

        {/* Weather Badge (optional bottom section) */}
        {showAnalytics && weather && weather.condition && weather.temperature !== undefined && (
          <div className="flex justify-center mt-3 pt-3 border-t">
            <WeatherBadge 
              condition={weather.condition} 
              temperature={weather.temperature}
              size="sm"
            />
          </div>
        )}

        {/* Click hint */}
        <div className="text-center mt-3">
          <Button variant="tertiary" className="fc-stretched-action" onClick={handleClick}
            aria-label={`View analytics for ${home_team} against ${away_team}`}>View match analytics</Button>
        </div>
      </Card>

      {/* Analytics Modal */}
      <MatchAnalyticsModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        fixture={fixture}
        analytics={analytics}
      />
    </>
  );
}
